import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send } from "lucide-react";
import { api, track } from "../lib/api";

const SUGGESTIONS = [
  "Tell me about Moringa Powder",
  "Do you offer wholesale?",
  "How do I become a distributor?",
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "assistant", text: "Hello! I'm Cultiva, your botanical assistant. Ask me about our products, ingredients, or wholesale & export partnerships." }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sid, setSid] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    track("assistant_message", {});
    try {
      const { data } = await api.post("/assistant", { session_id: sid, message: q });
      setSid(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMsgs((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. You can reach us on WhatsApp at +91 96232 99614 or support@cultivabotanics.com." }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        onClick={() => { setOpen(!open); if (!open) track("assistant_open", {}); }}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-botanical text-ivory flex items-center justify-center shadow-lg shadow-botanical/30 hover:bg-forest transition-colors"
        data-testid="assistant-toggle" aria-label="Open assistant"
      >
        <AnimatePresence mode="wait">
          {open ? <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ opacity: 0 }}><X className="h-6 w-6" /></motion.span>
                : <motion.span key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}><Sparkles className="h-6 w-6" /></motion.span>}
        </AnimatePresence>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] max-h-[70vh] bg-ivory border border-border rounded-lg shadow-2xl shadow-botanical/10 flex flex-col overflow-hidden"
            data-testid="assistant-panel"
          >
            <div className="bg-botanical text-ivory px-5 py-4 flex items-center gap-3">
              <span className="h-9 w-9 rounded-full bg-ivory/15 flex items-center justify-center"><Sparkles className="h-4 w-4" /></span>
              <div><div className="font-serif text-lg leading-none">Cultiva Assistant</div><div className="text-[11px] text-ivory/60 mt-0.5">Botanical & business enquiries</div></div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3 hide-scrollbar">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${m.role === "user" ? "bg-botanical text-ivory rounded-br-sm" : "bg-white border border-border text-forest rounded-bl-sm"}`} data-testid={`chat-msg-${m.role}`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start"><div className="bg-white border border-border rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                  {[0, 1, 2].map((d) => <motion.span key={d} className="h-2 w-2 rounded-full bg-earth" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }} />)}
                </div></div>
              )}
              {msgs.length === 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} className="text-xs border border-border rounded-full px-3 py-1.5 text-botanical/70 hover:border-botanical/50 hover:text-botanical transition-colors" data-testid="chat-suggestion">{s}</button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3 flex items-center gap-2">
              <input
                value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask about products or partnerships…"
                className="flex-1 bg-transparent outline-none text-sm text-forest placeholder:text-muted-foreground/60 px-2"
                data-testid="chat-input"
              />
              <button type="submit" disabled={loading || !input.trim()} className="h-9 w-9 rounded-full bg-botanical text-ivory flex items-center justify-center disabled:opacity-40 hover:bg-forest transition-colors" data-testid="chat-send">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
